import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { getListingPrice, resolveListingDataValue } from '@pkgs/components/utils/listingDataUtils';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { Copy } from '@ev-tech/ui-core-components';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useViewportIsBetween } from '@/hooks/useScrollTriggers';
import { useListingPageComponentRefs } from '@/hooks/useListingPageComponentRefs';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';

import { PriceDetailsText } from './PriceDetailsText';
import { LocationText } from './LocationText';
import { ListingTitle } from './ListingTitle';
import { ShareButton } from '../ShareButton/ShareButton';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton';
import { ContactButtonsGroup } from '../ContactButtonsGroup/ContactButtonsGroup';
import { BackButton } from '../BackButton/BackButton';

import styles from './StickyHeader.module.css';

type StickyHeaderProps = {
  className?: string;
};

export const StickyHeader = ({ className }: StickyHeaderProps) => {
  const { t: tListingData } = useTranslation(TRANSLATION_NAMESPACE.listing_data);
  const { breakpoint } = useBreakpoint();

  const { actionBarRef, agentCardCtaRef, contactFormCtaRef } = useListingPageComponentRefs();
  const stickyHeaderTriggerRef = breakpoint && breakpoint <= Breakpoints.TABLET_LANDSCAPE ? actionBarRef : agentCardCtaRef;
  const isStickyHeaderVisible = useViewportIsBetween({ ref: stickyHeaderTriggerRef }, { ref: contactFormCtaRef });
  const { data } = useListing();
  const { currency, language } = useSettings();
  const priceDescriptor = data.listing ? getListingPrice(data.listing, { currency, language }) : undefined;
  const price = priceDescriptor ? resolveListingDataValue(priceDescriptor, tListingData) : '';
  const priceLabel = priceDescriptor ? tListingData(priceDescriptor.labelKey) : '';
  const { featureFlags } = useExposePageProps();
  const {
    data: { listing, genericListing },
  } = useListing();

  const shouldShowWhatsAppButton = featureFlags?.showWhatsAppButton && genericListing.shopWhatsappNumber;

  return (
    <div className={styles.stickyHeaderContainer}>
      <nav className={cn(styles.stickyHeader, className)} data-testid="expose-sticky-header" data-visible={isStickyHeaderVisible}>
        <div className={styles.innerGrid}>
          <BackButton className={styles.backButton} />
          <div className={styles.content}>
            <div className={styles.infoWrapper} data-shop-whatsapp-button={shouldShowWhatsAppButton}>
              <LocationText />
              <ListingTitle />
            </div>
            <div className={styles.priceWrapper}>
              {priceLabel && <Copy className={styles.infoLabel} text={priceLabel} size="m" />}
              <PriceDetailsText price={price} convertedPrice={priceDescriptor?.convertedValue} />
            </div>
            <div className={styles.buttonGroup} data-shop-whatsapp-button={shouldShowWhatsAppButton}>
              {isStickyHeaderVisible && <ShareButton size="medium" />}
              {listing && <FavoriteButton size="medium" listing={listing} />}
            </div>
            <div className={styles.contactInfoContainer}>
              <ContactButtonsGroup
                buttonGroupClassName={styles.buttonsContainer}
                testId="sticky-header_primary_request-expose"
                shouldShowStroke={true}
              />
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
